import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { buildWebSocketUrl } from "../config/api";
import NextBestAction from "./NextBestAction";
import NewsAIAnalysis from "./NewsAIAnalysis";

const RealtimeNews = ({ user, portfolioData, visions = [] }) => {
  const [articles, setArticles] = useState([]);
  const [status, setStatus] = useState("idle"); // idle, loading, completed, error
  const [message, setMessage] = useState("");
  const [progress, setProgress] = useState({ phase: "", current: 0, total: 100, message: "" });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [visibleCount, setVisibleCount] = useState(10);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const lastLoadTime = useRef(0);
  const LOAD_COOLDOWN = 60000; // 60 seconds minimum between loads
  const requestCount = useRef(0);
  const MAX_REQUESTS_PER_MINUTE = 5; // Reduced from 10
  const hasLoadedNews = useRef(false);

  const connectWebSocket = useCallback((sessionId) => {
    if (!sessionId) return;
    
    try {
      const wsUrl = buildWebSocketUrl(`/ws/news/${sessionId}`);
      
      console.log('Connecting to WebSocket:', wsUrl);
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        reconnectAttempts.current = 0;
        wsRef.current = ws;
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data);
          
          if (data.type === 'ping') {
            // Respond to ping with pong
            ws.send(JSON.stringify({ type: 'pong', timestamp: new Date().toISOString() }));
            return;
          }
          
          if (data.type === 'pong') {
            // Server responded to our ping
            return;
          }
          
          // Handle news update
          if (data.status) {
            setStatus(data.status);
            setProgress(data.progress || {});
            setMessage(data.progress?.message || data.status);
            
            if (data.articles && data.articles.length > 0) {
              setArticles(data.articles);
              hasLoadedNews.current = true;
              console.log(`📰 Received ${data.articles.length} articles via WebSocket`);
            }
            
            if (data.updated_at) {
              setLastUpdated(new Date(data.updated_at));
            }
            
            if (data.error) {
              setMessage(`Error: ${data.error}`);
            }
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      
      ws.onclose = () => {
        console.log('WebSocket disconnected');
        wsRef.current = null;
        
        // Attempt to reconnect if we're still loading
        if (status === 'loading' && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current++;
          console.log(`Attempting to reconnect (${reconnectAttempts.current}/${maxReconnectAttempts})...`);
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket(sessionId);
          }, 2000 * reconnectAttempts.current);
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
    }
  }, [status]);

  const loadNews = useCallback(async (forceRefresh = false) => {
    try {
      // Check cooldown unless it's a forced refresh
      const now = Date.now();
      if (!forceRefresh && now - lastLoadTime.current < LOAD_COOLDOWN) {
        console.log('Skipping news load due to cooldown');
        return;
      }
      
      // Additional check to prevent rapid successive calls
      if (status === "loading") {
        console.log('News already loading, skipping duplicate request');
        return;
      }
      
      // If we already have news and it's not a forced refresh, don't load again
      if (!forceRefresh && hasLoadedNews.current && articles.length > 0) {
        console.log('News already loaded, skipping duplicate request');
        return;
      }
      
      // Check request rate limiting
      if (now - lastLoadTime.current < 60000) { // Within last minute
        if (requestCount.current >= MAX_REQUESTS_PER_MINUTE) {
          console.log('Too many requests, skipping to prevent rate limiting');
          setStatus("error");
          setMessage("Too many requests. Please wait before trying again.");
          return;
        }
      } else {
        // Reset counter if more than a minute has passed
        requestCount.current = 0;
      }
      
      requestCount.current++;
      
      lastLoadTime.current = now;
      setStatus("loading");
      setMessage("Loading news...");
      setArticles([]);

      // Ensure we have authentication
      const token = localStorage.getItem("authToken") || 
                   localStorage.getItem("token") || 
                   localStorage.getItem("access_token") ||
                   sessionStorage.getItem("token") ||
                   sessionStorage.getItem("access_token");

      if (!token) {
        setStatus("error");
        setMessage("No authentication token found. Please log in again.");
        return;
      }

      console.log('Loading news with WebSocket support...');
      
      // Get news immediately (returns cached or starts background fetch)
      const response = await axios.get('/api/news/websocket/get', {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        timeout: 10000 // Increased timeout for news fetching
      });
      
      if (response.data.status === 'success') {
        const newsData = response.data.data;
        const sessionId = response.data.session_id;
        
        if (newsData.cached && newsData.articles.length > 0) {
          // We have cached news, use it immediately
          setArticles(newsData.articles);
          setStatus("completed");
          setMessage(`Loaded ${newsData.articles.length} articles from cache`);
          setLastUpdated(new Date(newsData.fetched_at));
          hasLoadedNews.current = true;
        } else if (sessionId) {
          // News is being fetched, connect to WebSocket for real-time updates
          setSessionId(sessionId);
          setStatus("loading");
          setMessage("Fetching news in real-time...");
          setProgress(newsData.progress || {});
          
          // Connect to WebSocket for real-time updates
          connectWebSocket(sessionId);
        } else {
          // No news available
          setArticles([]);
          setStatus("completed");
          setMessage("No news available at this time");
        }
      } else if (response.data.status === 'rate_limited') {
        // Handle rate limiting response
        setStatus("error");
        setMessage(response.data.message || "Too many requests. Please wait before trying again.");
        setArticles([]);
        hasLoadedNews.current = false;
      }
    } catch (error) {
      console.error('Failed to load news:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        setStatus("error");
        setMessage("Request timed out. Please try again.");
      } else if (error.response?.status === 401) {
        setStatus("error");
        setMessage("Authentication failed. Please log in again.");
      } else if (error.response?.status === 500) {
        setStatus("error");
        setMessage("Server error. Please try again later.");
      } else {
        setStatus("error");
        setMessage(`Failed to load news: ${error.message}`);
      }
    }
  }, []); // Removed connectWebSocket dependency

  const refreshNews = useCallback(async () => {
    try {
      setIsRefreshing(true);
      setStatus("loading");
      setMessage("Refreshing news...");
      
      // Close existing WebSocket connection
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      
      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Reset session
      setSessionId(null);
      reconnectAttempts.current = 0;
      
      // Load fresh news with force refresh
      await loadNews(true);
    } catch (error) {
      console.error('Failed to refresh news:', error);
      setStatus("error");
      setMessage("Failed to refresh news");
    } finally {
      setIsRefreshing(false);
    }
  }, [loadNews]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  const hasPortfolio = user?.portfolio_connected || user?.onboarding_complete;
  useEffect(() => {
    if (hasPortfolio && user?.user_id) {
      const timeoutId = setTimeout(() => loadNews(), 500);
      return () => clearTimeout(timeoutId);
    } else {
      hasLoadedNews.current = false;
    }
  }, [hasPortfolio, user?.user_id]);

  const formatTime = (date) => {
    if (!date) return "";
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const getStatusIcon = () => {
    switch (status) {
      case "loading":
        return "⏳";
      case "completed":
        return "✅";
      case "error":
        return "❌";
      default:
        return "📰";
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "loading":
        return "text-yellow-400";
      case "completed":
        return "text-green-400";
      case "error":
        return "text-red-400";
      default:
        return "text-gray-400";
    }
  };

  if (!user) {
    return (
      <div className="surface-panel p-6">
        <div className="text-center text-secondary">
          <p>Loading user data...</p>
        </div>
      </div>
    );
  }

  if (!hasPortfolio) {
    return (
      <div className="surface-panel p-6">
        <div className="text-center text-secondary">
          <p>Connect your portfolio to get personalized news</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Status Bar */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${getStatusColor()}`}>
            {getStatusIcon()} {message || 'Ready'}
          </span>
          {wsRef.current && (
            <span className="text-xs px-2 py-1 bg-pine-tint text-pine rounded-full border border-hairline">
              Live
            </span>
          )}
          {lastUpdated && (
            <span className="text-xs text-faint">
              • Updated {formatTime(lastUpdated)}
            </span>
          )}
        </div>
        
        <button
          onClick={refreshNews}
          disabled={isRefreshing || status === "loading"}
          className="btn-secondary text-xs disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRefreshing ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* Next Best Action */}
      {portfolioData && (
        <div className="mb-6">
          <NextBestAction portfolioData={portfolioData} news={articles} />
        </div>
      )}

      {/* Progress Bar */}
      {status === "loading" && progress.total > 0 && (
        <div className="mb-4">
          <div className="flex justify-between text-xs text-faint mb-1">
            <span>{progress.phase || "Loading..."}</span>
            <span>{progress.current}/{progress.total}</span>
          </div>
          <div className="progress-bar-track">
            <div 
              className="progress-bar-fill"
              style={{ width: `${(progress.current / progress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {status === "loading" && articles.length === 0 && (
        <div className="space-y-3">
          <div className="animate-pulse">
            <div className="h-4 bg-hairline rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-hairline rounded w-1/2"></div>
          </div>
          <div className="animate-pulse">
            <div className="h-4 bg-white/10 rounded w-2/3 mb-2"></div>
            <div className="h-3 bg-white/5 rounded w-3/4"></div>
          </div>
        </div>
      )}

      {/* Error State */}
      {status === "error" && (
        <div className="text-center py-8">
          <p className="text-danger mb-2 font-medium">Error</p>
          <p className="text-secondary mb-4">{message}</p>
          {message.includes("Too many requests") ? (
            <div className="text-sm text-faint mb-4">
              Please wait a moment before trying again. The system is protecting against too many requests.
            </div>
          ) : null}
          <button
            onClick={() => loadNews(true)}
            className="btn-secondary"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Articles */}
      {status === "completed" && articles.length > 0 && (
        <div className="space-y-4">
          {articles.slice(0, visibleCount).map((article, index) => (
            <div key={index} className="border-b border-hairline pb-4 last:border-b-0">
              <div className="flex items-start justify-between mb-2">
                <h4 className="text-primary font-medium text-sm leading-tight flex-1 mr-2">
                  {article.title}
                </h4>
                <span className="text-xs text-faint whitespace-nowrap">
                  {article.source}
                </span>
              </div>
              
              {article.summary && (
                <p className="text-secondary text-xs mb-2 line-clamp-2">
                  {article.summary}
                </p>
              )}
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {article.category && (
                    <span className="topic-tag text-xs">
                      {article.category.replace('_', ' ')}
                    </span>
                  )}
                  {article.sentiment && (
                    <span className={`px-2 py-1 rounded-md text-xs border ${
                      article.sentiment === 'positive' ? 'border-pine bg-pine-tint text-pine' :
                      article.sentiment === 'negative' ? 'border-danger/40 bg-pine-tint text-danger' :
                      'border-hairline bg-bone text-secondary'
                    }`}>
                      {article.sentiment}
                    </span>
                  )}
                </div>
                
                {article.url && (
                  <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-pine hover:text-primary transition-colors duration-300"
                  >
                    Read →
                  </a>
                )}
              </div>
              
              {/* AI Analysis Section */}
              {user?.user_id && (
                <NewsAIAnalysis 
                  newsItem={article} 
                  user={user} 
                  portfolioData={portfolioData}
                  visions={visions}
                />
              )}
            </div>
          ))}
          
          {/* Load More Button */}
          {visibleCount < articles.length && (
            <div className="pt-4">
              <button
                onClick={() => setVisibleCount(n => n + 10)}
                className="btn-secondary"
              >
                Load {Math.min(10, articles.length - visibleCount)} more
              </button>
            </div>
          )}
        </div>
      )}

      {/* No Articles */}
      {status === "completed" && articles.length === 0 && (
        <div className="text-center py-8">
          <p className="text-secondary">No news articles found</p>
        </div>
      )}
    </div>
  );
};

export default RealtimeNews;
